import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from '@heroui/modal';
import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { Input } from '@heroui/input';
import { FormEvent, useCallback, useState } from 'react';
import { Select, SelectItem } from '@heroui/select';
import { useTranslation } from 'react-i18next';

import { SongbookColor } from '@/core/db.ts';
import { useAddSongbook } from '@/hooks/useSongbook.ts';

import ColorIcon from './ColorIcon.tsx';

const colors: SongbookColor[] = ['default', 'primary', 'secondary', 'success', 'danger', 'warning'];

interface SongbookCreateModalProps {
  disclosure: ReturnType<typeof useDisclosure>;
}

function SongbookCreateModal({ disclosure }: SongbookCreateModalProps) {
  const { t } = useTranslation();
  const { isOpen, onOpenChange, onClose } = disclosure;
  const [color, setColorValue] = useState(colors[0]);
  const { mutateAsync: addSongbook, isPending } = useAddSongbook();

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const data = new FormData(e.target as HTMLFormElement);

    await addSongbook({ title: data.get('title')?.toString() || '', color });
    onClose();
  }, [color, onClose, addSongbook]);

  return (
    <Modal backdrop="blur" isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent className="bg-default-50 text-foreground">
        {(onClose) => (
          <Form onSubmit={handleSubmit}>
            <div className="w-full">
              <ModalHeader className="flex flex-col gap-1">{t('songbook.modal.createTitle')}</ModalHeader>
              <ModalBody>
                <Input
                  isRequired
                  disabled={isPending}
                  errorMessage={t('songbook.modal.titleError')}
                  label={t('songbook.modal.titleLabel')}
                  labelPlacement="outside"
                  name="title"
                  placeholder={t('songbook.modal.titlePlaceholder')}
                  type="text"
                />
                <Select
                  isRequired
                  color="default"
                  disabled={isPending}
                  label={t('songbook.modal.colorLabel')}
                  labelPlacement="outside"
                  maxListboxHeight={400}
                  placeholder={t('songbook.modal.colorPlaceholder')}
                  selectedKeys={[color]}
                  selectionMode="single"
                  startContent={<ColorIcon c={color} p={0}/>}
                  variant="bordered"
                  onChange={(e) => setColorValue(e.target.value as SongbookColor)}
                >
                  {colors.map((c) => (
                    <SelectItem
                      key={c}
                      className="text-foreground"
                      startContent={<ColorIcon c={c} s={5}/>}
                      textValue=" "
                    />
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                <Button color="default" disabled={isPending} variant="light" onPress={onClose}>
                  {t('songbook.modal.cancel')}
                </Button>
                <Button color="primary" disabled={isPending} type="submit">
                  {t('songbook.modal.create')}
                </Button>
              </ModalFooter>
            </div>
          </Form>
        )}
      </ModalContent>
    </Modal>
  );
}

export default SongbookCreateModal;
